import { LeaderboardEntry, CrossoverTeamMemberActivity, DailyUsageData } from '../types';
import { calculateTimeStats, calculateTimeStatsForPeriod, aggregateTimeStats } from './timeCalculationService';
import { formatTimeMinutes } from './personalizedDataService';

export interface AIEffectivenessMetrics {
  // Cursor Efficiency Sub-Score
  aiAssistanceRatio: number; // acceptedLinesAdded / totalLinesAdded * 100
  productivityScore: number; // totalLinesAdded / cursorTimeHours normalized to 0-100
  cursorEfficiency: number; // Combined cursor efficiency score (0-100)
  
  // AI Tool Usage Effectiveness Sub-Score  
  aiAdoptionScore: number; // aiToolTime / (aiToolTime + nonAIToolTime) * 100
  aiToolEffectiveness: number; // AI tool effectiveness score (0-100) - same as aiAdoptionScore
  
  // Combined Score Components
  totalAITime: number; // Cursor time + AI tool time (minutes)
  totalWorkTime: number; // Total AI time + Non-AI tool time (minutes)
  overallAIAdoption: number; // totalAITime / totalWorkTime * 100
  
  // Final Score
  aiEffectivenessScore: number; // Final combined score (0-100)
  
  // Supporting Data
  cursorTimeHours: number;
  aiToolTimeHours: number;
  nonAIToolTimeHours: number;
  uniqueAIToolsCount: number;
  primaryAITool: string;
  
  // Raw Cursor Metrics (4-week totals)
  acceptedLinesAdded: number;
  totalLinesAdded: number;
}

export interface TeamAIMemberData {
  userId: string;
  email: string;
  name: string;
  metrics: AIEffectivenessMetrics;
  periodWeeks: string[]; // Array of week dates included in calculation
  dataCompleteness: number; // Percentage of weeks with complete data (0-100)
}

export interface RollingPeriodInfo {
  startDate: string; // First Monday of the 4-week period
  endDate: string; // Last Sunday of the 4-week period  
  weekDates: string[]; // Array of Monday dates for each week
  currentWeekIncluded: boolean; // Whether current incomplete week is included
  mode: 'last-4-weeks' | 'current-board'; // Period calculation mode
}

export class TeamAIDataService {
  
  /**
   * Calculate rolling 4-week period dates based on the mode
   */
  static calculateRollingPeriod(selectedWeek: string, mode: 'last-4-weeks' | 'current-board' = 'current-board'): RollingPeriodInfo {
    const currentDate = new Date();
    
    // Calculate current week's Monday
    const currentMonday = new Date(currentDate);
    currentMonday.setDate(currentDate.getDate() - currentDate.getDay() + 1);
    currentMonday.setHours(0, 0, 0, 0);
    
    const weekDates: string[] = [];
    let referenceDate: Date;
    
    if (mode === 'last-4-weeks') {
      // For "Last 4 Full Weeks": Always use 4 complete weeks ending before current week
      // Use the Monday of last week (most recent complete week)
      referenceDate = new Date(currentMonday);
      referenceDate.setDate(currentMonday.getDate() - 7); // Go back 1 week to get last complete week
    } else {
      // For "Current Board": Always use current Monday for rolling 4-week period
      referenceDate = new Date(currentMonday);
    }
    
    // Generate 4 consecutive week dates (Mondays) ending with referenceDate
    for (let i = 3; i >= 0; i--) {
      const weekDate = new Date(referenceDate);
      weekDate.setDate(referenceDate.getDate() - (i * 7));
      weekDates.push(weekDate.toISOString().split('T')[0]);
    }
    
    // Calculate period start (first Monday) and end (last Sunday)
    const startDate = weekDates[0];
    const endDate = new Date(weekDates[3]);
    endDate.setDate(endDate.getDate() + 6); // Add 6 days to get Sunday
    
    // Determine if current week is included based on mode
    const currentWeekMonday = currentMonday.toISOString().split('T')[0];
    let currentWeekIncluded: boolean;
    
    if (mode === 'last-4-weeks') {
      // Last 4 weeks mode never includes current week (by definition)
      currentWeekIncluded = false;
    } else {
      // Current board mode includes current week if it's in the period
      currentWeekIncluded = weekDates.includes(currentWeekMonday);
    }
    
    console.log('🏆 [PERIOD-CALC]', {
      mode,
      currentMonday: currentWeekMonday,
      weekDates,
      currentWeekIncluded,
      startDate,
      endDate: endDate.toISOString().split('T')[0]
    });
    
    return {
      startDate,
      endDate: endDate.toISOString().split('T')[0],
      weekDates,
      currentWeekIncluded,
      mode
    };
  }
  
  /**
   * Aggregate 4 weeks of cursor and crossover data for team AI leaderboard
   */
  static async processTeamAIData(
    cursorDataByWeek: (LeaderboardEntry[] | null)[],
    crossoverDataByWeek: (CrossoverTeamMemberActivity[] | null)[],
    periodInfo: RollingPeriodInfo
  ): Promise<TeamAIMemberData[]> {
    
    console.log('🏆 Processing Team AI data for period:', periodInfo);
    
    // Build user map from crossover data ONLY (team members are the master list)
    const userMap = new Map<string, {
      email: string;
      name: string;
      cursorWeeks: { [week: string]: LeaderboardEntry };
      crossoverWeeks: { [week: string]: CrossoverTeamMemberActivity };
    }>();
    
    // Process crossover data by week to build the master user list
    crossoverDataByWeek.forEach((weekData, index) => {
      const weekDate = periodInfo.weekDates[index];
      if (!weekData) return;
      
      weekData.forEach(member => {
        // Use the same email extraction method as the working implementation
        const email = (member as any).assignmentHistory?.companyEmail || '';
        if (!email) return;
        
        if (!userMap.has(email)) {
          userMap.set(email, {
            email,
            name: member.assignment?.candidate?.printableName || email.split('@')[0],
            cursorWeeks: {},
            crossoverWeeks: {}
          });
        }
        
        const userData = userMap.get(email)!;
        userData.name = member.assignment?.candidate?.printableName || userData.name;
        userData.crossoverWeeks[weekDate] = member;
      });
    });
    
    // Match cursor data to existing team members only
    cursorDataByWeek.forEach((weekData, index) => {
      const weekDate = periodInfo.weekDates[index];
      if (!weekData) return;
      
      weekData.forEach(entry => {
        // Only add cursor data if the user exists in crossover data (team members)
        if (userMap.has(entry.email)) {
          userMap.get(entry.email)!.cursorWeeks[weekDate] = entry;
        }
        // Ignore cursor-only users who are not team members
      });
    });
    
    // Calculate AI effectiveness metrics for each user
    const teamMembers: TeamAIMemberData[] = [];
    
    for (const [email, userData] of userMap.entries()) {
      const metrics = this.calculateAIEffectivenessMetrics(
        userData.cursorWeeks,
        userData.crossoverWeeks,
        periodInfo.weekDates
      );
      
      // Calculate data completeness
      const cursorWeeksCount = Object.keys(userData.cursorWeeks).length;
      const crossoverWeeksCount = Object.keys(userData.crossoverWeeks).length;
      const maxWeeks = periodInfo.weekDates.length;
      const dataCompleteness = Math.round(
        ((cursorWeeksCount + crossoverWeeksCount) / (maxWeeks * 2)) * 100
      );
      
      teamMembers.push({
        userId: email, // Use email as userId for consistency
        email,
        name: userData.name,
        metrics,
        periodWeeks: periodInfo.weekDates,
        dataCompleteness
      });
    }
    
    // Sort by AI Effectiveness Score (descending)
    teamMembers.sort((a, b) => b.metrics.aiEffectivenessScore - a.metrics.aiEffectivenessScore);
    
    console.log(`🏆 Processed ${teamMembers.length} team members for Team AI leaderboard`);
    
    return teamMembers;
  }
  
  /**
   * Calculate AI Effectiveness Score and supporting metrics
   */
  private static calculateAIEffectivenessMetrics(
    cursorWeeks: { [week: string]: LeaderboardEntry },
    crossoverWeeks: { [week: string]: CrossoverTeamMemberActivity },
    weekDates: string[]
  ): AIEffectivenessMetrics {
    
    // Aggregate cursor metrics across 4 weeks
    let totalAcceptedLinesAdded = 0;
    let totalLinesAdded = 0;
    
    Object.values(cursorWeeks).forEach(entry => {
      totalAcceptedLinesAdded += entry.chatAcceptedLinesAdded || 0;
      totalLinesAdded += entry.totalLinesAdded || 0;
    });
    
    // Aggregate time metrics across 4 weeks using timeCalculationService
    let totalCursorTimeMinutes = 0;
    let totalAIToolTimeMinutes = 0;
    let totalNonAIToolTimeMinutes = 0;
    const aiToolsUsed = new Set<string>();
    let primaryAIToolTime = 0;
    let primaryAITool = 'None';
    
    Object.values(crossoverWeeks).forEach(member => {
      if (!member.grouping?.advancedGroups) return;
      
      const timeStats = calculateTimeStats(member);
      
      totalCursorTimeMinutes += timeStats.cursorTime;
      totalAIToolTimeMinutes += timeStats.aiTime;
      totalNonAIToolTimeMinutes += timeStats.nonAiTime;
      
      // Track AI tools used (excluding cursor)
      member.grouping.advancedGroups.forEach(group => {
        if (group.sectionName === 'AI' || group.sectionName === 'PURE_AI') {
          group.groupItems.forEach(app => {
            if (app.applicationName && app.applicationName.toLowerCase() !== 'cursor') {
              aiToolsUsed.add(app.applicationName);
              
              // Track primary AI tool (most used non-cursor AI tool)
              if (app.spentTime > primaryAIToolTime) {
                primaryAIToolTime = app.spentTime;
                primaryAITool = app.applicationName;
              }
            }
          });
        }
      });
    });
    
    // Convert minutes to hours
    const cursorTimeHours = totalCursorTimeMinutes / 60;
    const aiToolTimeHours = totalAIToolTimeMinutes / 60;
    const nonAIToolTimeHours = totalNonAIToolTimeMinutes / 60;
    
    // Calculate total AI time (cursor + other AI tools)
    const totalAITimeMinutes = totalCursorTimeMinutes + totalAIToolTimeMinutes;
    const totalWorkTimeMinutes = totalAITimeMinutes + totalNonAIToolTimeMinutes;
    
    // 1. Cursor Efficiency Sub-Score
    const aiAssistanceRatio = totalLinesAdded > 0 
      ? (totalAcceptedLinesAdded / totalLinesAdded) * 100 
      : 0;
    
    const productivityScore = cursorTimeHours > 0 
      ? Math.min(100, Math.max(0, ((totalLinesAdded / cursorTimeHours) - 50) / 450 * 100)) // 50 lines/hour = 0 points, 500 lines/hour = 100 points
      : 0;
    
    const cursorEfficiency = (aiAssistanceRatio / 100) * productivityScore;
    
    // 2. AI Tool Usage Effectiveness Sub-Score
    const totalNonCursorTime = totalAIToolTimeMinutes + totalNonAIToolTimeMinutes;
    const aiAdoptionScore = totalNonCursorTime > 0 
      ? (totalAIToolTimeMinutes / totalNonCursorTime) * 100 
      : 0;
    
    const aiToolEffectiveness = aiAdoptionScore;
    
    // 3. Combined AI Effectiveness Score
    const overallAIAdoption = totalWorkTimeMinutes > 0 
      ? (totalAITimeMinutes / totalWorkTimeMinutes) * 100 
      : 0;
    
    // Calculate base performance score with dynamic weighting
    let basePerformanceScore: number;
    if (totalAITimeMinutes === 0) {
      basePerformanceScore = 0; // No AI usage
    } else if (totalCursorTimeMinutes === 0) {
      basePerformanceScore = aiToolEffectiveness; // AI-tool-only user
    } else if (totalAIToolTimeMinutes === 0) {
      basePerformanceScore = cursorEfficiency; // Cursor-only user
    } else {
      // Balanced user - weighted average based on actual usage
      const cursorWeight = totalCursorTimeMinutes / totalAITimeMinutes;
      const aiToolWeight = totalAIToolTimeMinutes / totalAITimeMinutes;
      basePerformanceScore = (cursorEfficiency * cursorWeight) + (aiToolEffectiveness * aiToolWeight);
    }
    
    // Final combined score
        const aiEffectivenessScore = (basePerformanceScore * 0.7) + (overallAIAdoption * 0.3);
    
    return {
      // Cursor Efficiency Sub-Score
      aiAssistanceRatio: Math.round(aiAssistanceRatio * 10) / 10,
      productivityScore: Math.round(productivityScore * 10) / 10,
      cursorEfficiency: Math.round(cursorEfficiency * 10) / 10,
      
      // AI Tool Usage Effectiveness Sub-Score
      aiAdoptionScore: Math.round(aiAdoptionScore * 10) / 10,
      aiToolEffectiveness: Math.round(aiToolEffectiveness * 10) / 10,
      
      // Combined Score Components
      totalAITime: totalAITimeMinutes,
      totalWorkTime: totalWorkTimeMinutes,
      overallAIAdoption: Math.round(overallAIAdoption * 10) / 10,
      
      // Final Score
      aiEffectivenessScore: Math.round(aiEffectivenessScore * 10) / 10,
      
      // Supporting Data
      cursorTimeHours: Math.round(cursorTimeHours * 10) / 10,
      aiToolTimeHours: Math.round(aiToolTimeHours * 10) / 10,
      nonAIToolTimeHours: Math.round(nonAIToolTimeHours * 10) / 10,
      uniqueAIToolsCount: aiToolsUsed.size,
      primaryAITool: primaryAITool,
      
      // Raw Cursor Metrics
      acceptedLinesAdded: totalAcceptedLinesAdded,
      totalLinesAdded: totalLinesAdded
    };
  }
  
  /**
   * Get AI effectiveness level label based on score
   */
  static getAIEffectivenessLevel(score: number): { label: string; color: string } {
    if (score >= 85) return { label: 'AI Expert', color: '#22c55e' };
    if (score >= 70) return { label: 'AI Power User', color: '#84cc16' };
    if (score >= 55) return { label: 'AI Proficient', color: '#eab308' };
    if (score >= 40) return { label: 'AI Developing', color: '#f97316' };
    if (score >= 25) return { label: 'AI Beginner', color: '#ef4444' };
    return { label: 'AI Minimal', color: '#6b7280' };
  }
  
  /**
   * Format time display consistently
   */
  static formatTime(minutes: number): string {
    return formatTimeMinutes(Math.round(minutes));
  }
}

export default TeamAIDataService;
